import type {
  StaticDatabase,
  StaticDatabaseConnection,
} from "@-/dev.improve.haniverse.database.static";
import { useCallback, useMemo } from "react";
import { withDatabaseLoggerProxy } from "@-/dev.improve.haniverse.lib.database.logger";
import { databaseLoggerFromLocation } from "@-/dev.improve.haniverse.lib.logger";
import {
  useGetStaticDatabase,
  useGetStaticDatabaseConnection,
  useStaticDatabaseGeneration,
} from "#use-static-database";
import { resolveHanWorkerUrl } from "@-/dev.improve.haniverse.lib.worker";

export function useStaticDatabaseContextValue(): {
  /** Active online/pack identity, included in static query keys. */
  dataIdentity: string;
  db: StaticDatabase;
  dbConnection: StaticDatabaseConnection;
  getOnlineWorkerUrl?: () => Promise<string | null>;
} {
  const dataIdentity = useStaticDatabaseGeneration();
  const getStaticDatabase = useGetStaticDatabase();
  const getStaticDatabaseConnection = useGetStaticDatabaseConnection();
  // oxlint-disable-next-line han-no-context-callbacks/no-callbacks-in-context-value -- stable transport capability; test/local providers omit it
  const getOnlineWorkerUrl = useCallback(async () => {
    if ((await getStaticDatabaseConnection()) !== null) {
      return null;
    }
    return resolveHanWorkerUrl();
  }, [getStaticDatabaseConnection]);

  // oxlint-disable-next-line han-no-context-callbacks/no-callbacks-in-context-value -- stable logger-proxy factory keyed on getStaticDatabase
  const db = useCallback<StaticDatabase>(
    (location) => withDatabaseLoggerProxy(getStaticDatabase, databaseLoggerFromLocation(location)),
    [getStaticDatabase],
  );
  const dbConnection = useMemo(
    () =>
      withDatabaseLoggerProxy(async (): Promise<StaticDatabaseConnection> => {
        // `bundled`/`offline` always resolve a local connection; only "online"
        // mode has none. No production code reads the static connection, and it
        // is never read while in online mode, so surface a clear error rather
        // than widen the context type to nullable.
        const connection = await getStaticDatabaseConnection();
        if (connection === null) {
          throw new Error("static database connection is unavailable in online mode");
        }
        return connection;
      }, null),
    [getStaticDatabaseConnection],
  );

  return {
    dataIdentity,
    db,
    dbConnection,
    getOnlineWorkerUrl,
  };
}
